import * as Types from '@aeppic/types'

import { EventEmitter } from  '@aeppic/shared/event-emitter'
import { getDynamicComponentBuildLog } from 'aeppic/dynamic/dynamic-component-builder.js'
import DynamicComponent from 'aeppic/dynamic/dynamic-component.js'

export type REMOTE_DEVELOPER_MODE = 'None' | 'Client' | 'Server'
export type WIREFRAME_TARGET = 'page' | 'app' | 'developer' | string

const DEFAULT_FORM_SECTION_LAYOUT_ID = '4dfdf094-32b3-41fe-a2eb-50a809c39d7d'

export default class Developer extends EventEmitter {

  private Aeppic = null
  private _aeppicServerAdapter
  private _remoteDeveloperMode: REMOTE_DEVELOPER_MODE = 'None'
  private _showMissingDesignFallbacks = false
  private _wireframeMode = false
  private _wireframeTarget: WIREFRAME_TARGET = 'page'

  private _discoveredElements: HTMLElement[] = []
  private _inspectorDiscoveryPath = []

  constructor(Aeppic) {
    super()

    this.Aeppic = Aeppic
    this._aeppicServerAdapter = Aeppic._serverAdapter

    this._setupRemoteDeveloper()
  }

  destroy() {
    this.removeAllListeners()
  }

  get isServerInRemoteDeveloperMode() {
    return this._remoteDeveloperMode === 'Server'
  }

  get isEnabled() {
    return this._remoteDeveloperMode !== 'None' || this.Aeppic.Account?.data?.allowDevMode === true
  }

  get showMissingDesignFallbacks() {
    return this._showMissingDesignFallbacks
  }

  set showMissingDesignFallbacks(showMissingDesignFallbacks: boolean) {
    this._showMissingDesignFallbacks = showMissingDesignFallbacks === true
  }

  get wireframeMode() {
    return this._wireframeMode
  }

  get wireframeTarget() {
    return this._wireframeTarget
  }

  set wireframeTarget(target: WIREFRAME_TARGET) {
    this._wireframeTarget = target || 'page'
    this.emit('wireframe-mode-changed', {
      enabled: this._wireframeMode,
      target: this._wireframeTarget
    })
  }

  enableMissingDesignFallbacks() {
    this.showMissingDesignFallbacks = true
  }

  disableMissingDesignFallbacks() {
    this.showMissingDesignFallbacks = false
  }

  enableWireframeMode(target: WIREFRAME_TARGET = this._wireframeTarget) {
    this._wireframeTarget = target || 'page'
    this._wireframeMode = true
    this.emit('wireframe-mode-changed', {
      enabled: true,
      target: this._wireframeTarget
    })
  }

  disableWireframeMode() {
    this._wireframeMode = false
    this.emit('wireframe-mode-changed', {
      enabled: false,
      target: this._wireframeTarget
    })
  }

  toggleWireframeMode(target: WIREFRAME_TARGET = this._wireframeTarget) {
    if (this._wireframeMode) {
      this.disableWireframeMode()
    } else {
      this.enableWireframeMode(target)
    }
  }

  public get DynamicComponentsBuilderInfo() {
    return {
      log: getDynamicComponentBuildLog()
    }
  }

  private _setupRemoteDeveloper() {
    if (!this._aeppicServerAdapter) {
      return
    }

    // server side
    this.Aeppic.Model.watchAllRevisions((document, reason) => {
      if (this._remoteDeveloperMode !== 'Server') {
        return
      }
      
      const simplifiedDocument = {
        id: document.id, 
        f: document.f,
        p: document.p,
        data: document.data
      }
      
      const data = {
        type: 'revision',
        document: simplifiedDocument,
        reason: reason
      }
      this._aeppicServerAdapter.sendToDeveloperWebsocket(data)
    })

    // client side
    this.on('inspector-path-updated', () => {
      if (this._remoteDeveloperMode !== 'Client') {
        return
      }

      const message = {
        type: 'inspector-path-updated',
        inspectorPath: this._inspectorDiscoveryPath
      }
      this._aeppicServerAdapter.sendToDeveloperWebsocket(message)
    })

    this._aeppicServerAdapter.on('developer:message', async (message) => {
      // client side
      if (message.type === 'revision' && this._remoteDeveloperMode === 'Client') {
        const editableDocument = await this.Aeppic.edit(message.document.id)
        if (editableDocument) {
          editableDocument.setData(message.document.data, { cloneFromDefault: false })
          this.Aeppic.Model.notifyWatchersAboutRevision(editableDocument, message.reason)
        }
      }
      // client side
      else if (message.type === 'navigate' && this._remoteDeveloperMode === 'Client') {
        this.Aeppic.navigateTo(message.documentId)
      }
      // server side
      else if (message.type === 'inspector-path-updated' && this._remoteDeveloperMode === 'Server') {
        this.setInspectorDiscoveryPath(message.inspectorPath, [])
      }
      // both sides
      else if (message.type === 'remote-message') {
        this.emit(message.message, message.data)
      }
    })  
  }

  public enableRemoteDeveloper(sessionId: string, mode: REMOTE_DEVELOPER_MODE) {
    if (!this._aeppicServerAdapter) {
      console.error('No serverAdapter for remote Developer')
      return
    }

    if (this._remoteDeveloperMode !== 'None') {
      this._aeppicServerAdapter.disconnectDeveloper()
    }

    this._remoteDeveloperMode = mode
    this._aeppicServerAdapter.connectDeveloper(sessionId)
  }

  public disableRemoteDeveloper() {
    this._remoteDeveloperMode = 'None'

    if (this._aeppicServerAdapter) {
      this._aeppicServerAdapter.disconnectDeveloper()
    }
  }

  public get remoteDeveloperMode() {
    return this._remoteDeveloperMode
  }

  public navigateTo(documentIdentifier: Types.Document | Types.IdOrReference) {
    if (this._remoteDeveloperMode !== 'Server') {
      this.Aeppic.navigateTo(documentIdentifier)
    }
    else if (this._aeppicServerAdapter) {
      const documentId = typeof documentIdentifier === 'string' ? documentIdentifier : documentIdentifier.id

      const message = {
        type: 'navigate',
        documentId: documentId
      }
      this._aeppicServerAdapter.sendToDeveloperWebsocket(message)
    }
  }

  public remoteEmit(message, data) {
    const wsMessage = {
      type: 'remote-message',
      message: message,
      data: data
    }
    this._aeppicServerAdapter.sendToDeveloperWebsocket(wsMessage)
  }

  
  //
  // Inspector discovery path
  // ========================
  //

  get inspectorPath() {
    return this._inspectorDiscoveryPath
  }

  setInspectorDiscoveryPath(discoveryPathInfo, elements, options?) {
    this._discoveredElements = elements
    this._inspectorDiscoveryPath = discoveryPathInfo
    this.emit('inspector-path-updated', this._discoveredElements, options)
  }

  private getInspectedElementInfo(elementId) {
    for (let i = 0; i < this._inspectorDiscoveryPath.length; i++) {
      if (this._inspectorDiscoveryPath[i].id === elementId) {
        const element = this._discoveredElements[i]
        const rect = element.getBoundingClientRect()

        return {
          top: Math.floor(rect.top),
          left: Math.floor(rect.left),
          width: Math.floor(rect.width),
          height: Math.floor(rect.height)
        }
      }
    }
    return null
  }

  buildInspectorDiscoveryPath(elements: HTMLElement[]) {
    const path = []

    for (let el of elements) {
      let entry = this._buildInspectorDiscoveryPathEntry(el)
      path.push(entry)
    }

    return path
  }

  buildWireframeElementInfo(element: HTMLElement) {
    return this._buildInspectorDiscoveryPathEntry(element)
  }

  private _buildInspectorDiscoveryPathEntry(element) {
    let entry = {
      fullTag: this._elementToString(element),
      tagName: element.tagName && element.tagName.toLowerCase(),
      attributes: this._buildAttributesArrayForElement(element),
      type: 'Element',
      documentId: null,
      formId: null,
      designId : null,
      layoutId: null,
      listId: null,
      controlId: null,
      componentName: null,
      fieldName: null,
      params: null,
      documentTitle: null,
      formName: null,
      id: this.Aeppic.uuid()
    }

    const component = this.Aeppic.lookupComponentForElement(element)
    const dynamicComponent = this._getDynamicComponent(component)

    if (this._elementHasClass(element, 'ae-design')) {
      entry.type = 'Design'
      entry.designId = dynamicComponent && dynamicComponent && dynamicComponent.document.id
      entry.documentId = component && component.documentToBeRendered && component.documentToBeRendered.id
      const formId = component && component.documentToBeRendered && component.documentToBeRendered.f.id
      entry.formId = formId
      entry.componentName = dynamicComponent?.document?.data?.name ?? null
      entry.documentTitle = component?.documentToBeRendered?.data?.name ?? null
      entry.formName = component?.documentToBeRendered?.f?.text ?? null
    }
    else if (this._elementHasClass(element, 'ae-list')) {
      entry.type = 'List'
      entry.listId = dynamicComponent && dynamicComponent && dynamicComponent.document.id
      entry.componentName = dynamicComponent?.document?.data?.name ?? null
    }
    else if (this._elementHasClass(element, 'ae-form')) {
      entry.type = 'Form'
      entry.formId = component && component.formToRender && component.formToRender.id
      entry.layoutId = component && component.$children[0] && component.$children[0].dynamicComponent
                    && component.$children[0].dynamicComponent.document && component.$children[0].dynamicComponent.document.id
      entry.formName = component?.formToRender?.data?.name ?? null
      entry.documentId = component?.document?.id ?? null
      entry.params = this._safeParams(component?.params)
    }
    else if (this._elementHasClass(element, 'ae-form--layout')) {
      // ae-form renders transparently as ae-layout with ae-form--layout class
      // lookupComponentForElement may return ae-design-selector or ae-form — walk to find ae-form
      const fc = this._findComponentWithProperty(component, 'formToRender')
      entry.type = 'Form'
      entry.formId = fc?.formToRender?.id ?? null
      entry.layoutId = fc?.formLayoutId ?? dynamicComponent?.document?.id ?? null
      entry.documentId = fc?.renderedDocument?.id ?? fc?.documentToBindTo?.id ?? null

      // Build params from ae-form's own props
      const formParams: Record<string, any> = {}
      if (fc?.readonly != null) formParams.readonly = fc.readonly
      if (fc?.autoSave != null) formParams.autoSave = fc.autoSave
      if (fc?.viewToUse) formParams.view = fc.viewToUse
      const attrParams = this._safeParams(fc?.$attrs ?? component?.$attrs)
      entry.params = Object.keys(formParams).length > 0 ? { ...formParams, ...(attrParams || {}) } : attrParams
    }
    else if (this._elementHasClass(element, 'ae-form-section')) {
      const sectionComponent = component || this._findClosestComponentForElement(element, owner =>
        owner?.section || owner?.selectedTab || owner?.rootSection
      )
      const section = sectionComponent?.section
        ?? (element.hasAttribute('data-ae-form-active-tab-section-key') ? sectionComponent?.selectedTab : null)
      const sectionTheme = section?.level != null ? sectionComponent?.theme?.sections?.levels?.[section.level] : null

      entry.type = 'Form Section'
      entry.layoutId = dynamicComponent?.document?.id
        ?? sectionComponent?.layoutIdToUse
        ?? sectionComponent?.layoutId
        ?? sectionTheme?.layoutId
        ?? element.getAttribute('data-ae-form-section-layout-id')
        ?? DEFAULT_FORM_SECTION_LAYOUT_ID
      entry.componentName = dynamicComponent?.document?.data?.name
        ?? section?.title
        ?? element.getAttribute('data-ae-form-section-title')
        ?? element.getAttribute('data-ae-form-section-key')
        ?? null
      entry.documentId = sectionComponent?.document?.id ?? null
      entry.formId = sectionComponent?.form?.id ?? null
      entry.params = this._safeParams(sectionComponent?.params)
        || this._safeParams(sectionComponent?.$props)
        || this._safeParams(sectionComponent?.$attrs)
    }
    else if (this._elementHasClass(element, 'ae-form-paragraph')) {
      entry.type = 'Form Paragraph'
      entry.componentName = element.getAttribute('data-ae-paragraph-type') ?? 'paragraph'
      entry.params = this._safeParams(component?.$props)
    }
    else if (this._elementHasClass(element, 'ae-layout')) {
      entry.type = 'Layout'
      entry.layoutId = dynamicComponent && dynamicComponent.document && dynamicComponent.document.id
      entry.componentName = dynamicComponent?.document?.data?.name ?? null
      entry.params = this._safeParams(component?.params) || this._safeParams(component?.$attrs)
    }
    else if (this._elementHasClass(element, 'ae-control')) {
      entry.type = 'Control'
      entry.controlId = dynamicComponent && dynamicComponent.document && dynamicComponent.document.id
      entry.componentName = component?.name ?? null
      entry.fieldName = component?.fieldNameToBindTo ?? null
      entry.params = this._safeParams(component?.paramsToBindTo)
      entry.documentTitle = component?.documentToBindTo?.data?.name ?? null
      entry.formName = component?.formToBindTo?.data?.name ?? null
    }
    else if (this._elementHasClass(element, 'ae-splitter')) {
      entry.type = 'Splitter'
      entry.componentName = 'splitter'
      if (component?.$props) {
        const { split, margin, limitExpand } = component.$props
        entry.params = { split, margin, limitExpand }
      }
    }
    else if (this._elementHasClass(element, 'ae-tabs')) {
      entry.type = 'Tabs'
      entry.componentName = 'tabs'
    }
    else if (this._elementHasClass(element, 'ae-query')) {
      entry.type = 'Query'
    }

    // Fallback: detect dynamic components by their DynamicComponent type
    // when no CSS class matched (e.g. layouts without ae-layout class)
    if (entry.type === 'Element' && dynamicComponent?.type) {
      if (dynamicComponent.type === 'layout') {
        entry.type = 'Layout'
        entry.layoutId = dynamicComponent.document?.id ?? null
        entry.componentName = dynamicComponent.document?.data?.name ?? null
        entry.params = this._safeParams(component?.params) || this._safeParams(component?.$attrs)
      } else if (dynamicComponent.type === 'control') {
        entry.type = 'Control'
        entry.controlId = dynamicComponent.document?.id ?? null
        entry.componentName = component?.name ?? null
        entry.fieldName = component?.fieldNameToBindTo ?? null
        entry.params = this._safeParams(component?.paramsToBindTo)
      } else if (dynamicComponent.type === 'design') {
        entry.type = 'Design'
        entry.designId = dynamicComponent.document?.id ?? null
        entry.documentId = component?.documentToBeRendered?.id ?? null
        entry.formId = component?.documentToBeRendered?.f?.id ?? null
        entry.componentName = dynamicComponent.document?.data?.name ?? null
      }
    }

    return entry
  }

  private _elementToString(element) {
    let attributes = ''
    for (let a of (element.attributes || [])) {
      attributes += ` ${a.name}=${a.value}`
    }

    return `<${element.tagName && element.tagName.toLowerCase()}${attributes}>`
  }

  private _buildAttributesArrayForElement(element) {
    const result = []

    for (let a of (element.attributes || [])) {
      result.push({
        name: a.name,
        value: a.value
      })
    }

    return result
  }

  private _elementHasClass(element: HTMLElement, className: string) {
    return element && element.classList && element.classList.contains(className)
  }

  private _findComponentWithProperty(component: any, property: string, maxDepth = 5): any {
    if (!component) return null
    if (component[property] !== undefined && component[property] !== null) return component

    if (maxDepth > 0 && component.$children) {
      for (const child of component.$children) {
        if (child.$el === component.$el) {
          const found = this._findComponentWithProperty(child, property, maxDepth - 1)
          if (found) return found
        }
      }
    }

    return null
  }

  private _findClosestComponentForElement(element: HTMLElement, predicate: (component: any) => boolean): any {
    let closestComponent = null
    let closestDepth = -1

    for (const component of this.Aeppic._recurseComponents()) {
      if (!component?.$el || component.$el === element || !component.$el.contains?.(element)) {
        continue
      }

      if (!predicate(component)) {
        continue
      }

      const depth = this._elementDepth(component.$el)
      if (depth > closestDepth) {
        closestDepth = depth
        closestComponent = component
      }
    }

    return closestComponent
  }

  private _elementDepth(element: HTMLElement) {
    let depth = 0
    let current = element

    while (current?.parentElement) {
      depth += 1
      current = current.parentElement
    }

    return depth
  }

  private _safeParams(obj: any): Record<string, any> | null {
    if (!obj || typeof obj !== 'object') return null

    const result: Record<string, any> = {}
    let hasKeys = false

    for (const rawKey of Object.keys(obj)) {
      let key: string
      if (rawKey.startsWith('param-')) {
        key = rawKey.slice(6)
      } else if (rawKey.includes('-')) {
        // Skip non-param hyphenated attrs like layout-id, watch-layout-revisions
        continue
      } else {
        key = rawKey
      }

      const val = obj[rawKey]

      if (val === null || val === undefined) {
        result[key] = val
        hasKeys = true
      } else if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        result[key] = val
        hasKeys = true
      } else if (Array.isArray(val)) {
        try {
          result[key] = JSON.parse(JSON.stringify(val))
          hasKeys = true
        } catch {
          result[key] = `[Array(${val.length})]`
          hasKeys = true
        }
      } else if (typeof val === 'object') {
        // Check for Aeppic documents (have .id and .f)
        if (val.id && val.f) {
          result[key] = val.id
          hasKeys = true
        } else {
          try {
            const serialized = JSON.stringify(val)
            if (serialized.length < 200) {
              result[key] = JSON.parse(serialized)
            } else {
              result[key] = '{...}'
            }
            hasKeys = true
          } catch {
            result[key] = '[Object]'
            hasKeys = true
          }
        }
      }
    }

    return hasKeys ? result : null
  }

  private _getDynamicComponent(component: any) {
    if (!component) {
      return null
    }

    const direct = component.dynamicComponent 

    if (direct) {
      return direct
    }

    if (component.$children?.length === 1) {
      const [ singleChild ] = component.$children
      return singleChild.dynamicComponent
    }

    return null
  }
}
